import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { CustomPlaceholder, PlaceholderPaletteItem } from '../../types/customDocuments';

// ============================================================
// Static catalogues
// labelKey = i18n key under admin.customDocuments.editor.palette.placeholders.*
// label   = English fallback (used if key missing from locale)
// ============================================================

const P = 'admin.customDocuments.editor.palette.placeholders';

const TRADE_FIELD_PLACEHOLDERS: PlaceholderPaletteItem[] = [
  { namespace: 'trade', id: 'TradeNumber',        label: 'Trade Number',        labelKey: `${P}.tradeNumber`,        token: '{{trade.TradeNumber}}',        type: 'string' },
  { namespace: 'trade', id: 'internalTradeRef.parentTradeNumber',  label: 'Parent Trade Number', labelKey: `${P}.parentTradeNumber`, token: '{{trade.internalTradeRef.parentTradeNumber}}',  type: 'string' },
  { namespace: 'trade', id: 'MirrorId',           label: 'Mirror ID',           labelKey: `${P}.mirrorId`,           token: '{{trade.MirrorId}}',           type: 'string' },
  { namespace: 'trade', id: 'TradeDate',          label: 'Trade Date',          labelKey: `${P}.tradeDate`,          token: '{{trade.TradeDate}}',          type: 'date'   },
  { namespace: 'trade', id: 'ValueDate',          label: 'Value Date',          labelKey: `${P}.valueDate`,          token: '{{trade.ValueDate}}',          type: 'date'   },
  { namespace: 'trade', id: 'PaymentDate',        label: 'Payment Date',        labelKey: `${P}.paymentDate`,        token: '{{trade.PaymentDate}}',        type: 'date'   },
  { namespace: 'trade', id: 'MaturityDate',       label: 'Maturity Date',       labelKey: `${P}.maturityDate`,       token: '{{trade.MaturityDate}}',       type: 'date'   },
  { namespace: 'trade', id: 'ProductType',        label: 'Product Type',        labelKey: `${P}.productType`,        token: '{{trade.ProductType}}',        type: 'string' },
  { namespace: 'trade', id: 'Direction',          label: 'Direction',           labelKey: `${P}.direction`,          token: '{{trade.Direction}}',          type: 'string' },
  { namespace: 'trade', id: 'QuantityCurrency1',   label: 'Amount',              labelKey: `${P}.amount`,             token: '{{trade.QuantityCurrency1}}',  type: 'number' },
  { namespace: 'trade', id: 'Currency1',          label: 'Currency 1',          labelKey: `${P}.currency1`,          token: '{{trade.Currency1}}',          type: 'string' },
  { namespace: 'trade', id: 'Price',              label: 'Price / Rate',        labelKey: `${P}.price`,              token: '{{trade.Price}}',              type: 'number' },
  { namespace: 'trade', id: 'SpotPrice',          label: 'Spot Price',          labelKey: `${P}.spotPrice`,          token: '{{trade.SpotPrice}}',          type: 'number' },
  { namespace: 'trade', id: 'ForwardPoints',      label: 'Forward Points',      labelKey: `${P}.forwardPoints`,      token: '{{trade.ForwardPoints}}',      type: 'number' },
  { namespace: 'trade', id: 'Currency2',          label: 'Currency 2',          labelKey: `${P}.currency2`,          token: '{{trade.Currency2}}',          type: 'string' },
  { namespace: 'trade', id: 'QuantityCurrency2',   label: 'Contra Amount',       labelKey: `${P}.contraAmount`,       token: '{{trade.QuantityCurrency2}}',  type: 'number' },
  { namespace: 'trade', id: 'SettlementType',     label: 'Settlement Type',     labelKey: `${P}.settlementType`,     token: '{{trade.SettlementType}}',     type: 'string' },
  { namespace: 'trade', id: 'SettlementCurrency', label: 'Settlement Currency', labelKey: `${P}.settlementCurrency`, token: '{{trade.SettlementCurrency}}', type: 'string' },
  { namespace: 'trade', id: 'CounterpartyName',   label: 'Counterparty',        labelKey: `${P}.counterparty`,       token: '{{trade.CounterpartyName}}',   type: 'string' },
  { namespace: 'trade', id: 'CounterpartyTaxId',  label: 'Counterparty Tax ID', labelKey: `${P}.counterpartyTaxId`,  token: '{{trade.CounterpartyTaxId}}',  type: 'string' },
  { namespace: 'trade', id: 'OurParty',           label: 'Our Party',           labelKey: `${P}.ourParty`,           token: '{{trade.OurParty}}',           type: 'string' },
  { namespace: 'trade', id: 'OurPartyTaxId',      label: 'Our Party Tax ID',    labelKey: `${P}.ourPartyTaxId`,      token: '{{trade.OurPartyTaxId}}',      type: 'string' },
  { namespace: 'trade', id: 'workflowState',      label: 'Workflow State',      labelKey: `${P}.workflowState`,      token: '{{trade.workflowState}}',      type: 'string' },
  { namespace: 'trade', id: 'Notes',              label: 'Notes',               labelKey: `${P}.notes`,              token: '{{trade.Notes}}',              type: 'string' },
  { namespace: 'trade', id: 'Grouping1',          label: 'Grouping 1',          labelKey: `${P}.grouping1`,          token: '{{trade.Grouping1}}',          type: 'string' },
  { namespace: 'trade', id: 'Grouping2',          label: 'Grouping 2',          labelKey: `${P}.grouping2`,          token: '{{trade.Grouping2}}',          type: 'string' },
  { namespace: 'trade', id: 'Grouping3',          label: 'Grouping 3',          labelKey: `${P}.grouping3`,          token: '{{trade.Grouping3}}',          type: 'string' },
  { namespace: 'trade', id: 'HedgeObject',        label: 'Hedge Object',        labelKey: `${P}.hedgeObject`,        token: '{{trade.HedgeObject}}',        type: 'string' },
  { namespace: 'trade', id: 'bankTradeNumber',    label: 'Counterparty Trade Number', labelKey: `${P}.bankTradeNumber`, token: '{{trade.bankTradeNumber}}',    type: 'string' },
  { namespace: 'trade', id: 'fxgoTradeId',       label: 'FXGO Reference',       labelKey: `${P}.fxgoTradeId`,       token: '{{trade.fxgoTradeId}}',       type: 'string' },
  { namespace: 'trade', id: 'FixingReference',    label: 'Fixing Reference',    labelKey: `${P}.fixingReference`,    token: '{{trade.FixingReference}}',    type: 'string' },
];

const SYSTEM_PLACEHOLDERS: PlaceholderPaletteItem[] = [
  { namespace: 'system', id: 'currentDate',     label: 'Current Date',      labelKey: `${P}.currentDate`,     token: '{{system.currentDate}}',     type: 'date' },
  { namespace: 'system', id: 'currentTime',     label: 'Current Time',      labelKey: `${P}.currentTime`,     token: '{{system.currentTime}}',     type: 'string' },
  { namespace: 'system', id: 'currentDateTime', label: 'Current Date/Time', labelKey: `${P}.currentDateTime`, token: '{{system.currentDateTime}}', type: 'string' },
];

const SETTLEMENT_PLACEHOLDERS: PlaceholderPaletteItem[] = [
  // Cargar (pay-out) account
  { namespace: 'cargarAccount', id: 'bankName',        label: 'Cargar: Bank Name',        labelKey: `${P}.cargarBankName`,        token: '{{cargarAccount.bankName}}',        type: 'string' },
  { namespace: 'cargarAccount', id: 'accountName',     label: 'Cargar: Account Name',     labelKey: `${P}.cargarAccountName`,     token: '{{cargarAccount.accountName}}',     type: 'string' },
  { namespace: 'cargarAccount', id: 'accountNumber',   label: 'Cargar: Account Number',   labelKey: `${P}.cargarAccountNumber`,   token: '{{cargarAccount.accountNumber}}',   type: 'string' },
  { namespace: 'cargarAccount', id: 'swiftCode',       label: 'Cargar: SWIFT Code',       labelKey: `${P}.cargarSwiftCode`,       token: '{{cargarAccount.swiftCode}}',       type: 'string' },
  { namespace: 'cargarAccount', id: 'accountCurrency', label: 'Cargar: Currency',         labelKey: `${P}.cargarCurrency`,        token: '{{cargarAccount.accountCurrency}}', type: 'string' },
  // Abonar (receive) account
  { namespace: 'abonarAccount', id: 'bankName',        label: 'Abonar: Bank Name',        labelKey: `${P}.abonarBankName`,        token: '{{abonarAccount.bankName}}',        type: 'string' },
  { namespace: 'abonarAccount', id: 'accountName',     label: 'Abonar: Account Name',     labelKey: `${P}.abonarAccountName`,     token: '{{abonarAccount.accountName}}',     type: 'string' },
  { namespace: 'abonarAccount', id: 'accountNumber',   label: 'Abonar: Account Number',   labelKey: `${P}.abonarAccountNumber`,   token: '{{abonarAccount.accountNumber}}',   type: 'string' },
  { namespace: 'abonarAccount', id: 'swiftCode',       label: 'Abonar: SWIFT Code',       labelKey: `${P}.abonarSwiftCode`,       token: '{{abonarAccount.swiftCode}}',       type: 'string' },
  { namespace: 'abonarAccount', id: 'accountCurrency', label: 'Abonar: Currency',         labelKey: `${P}.abonarCurrency`,        token: '{{abonarAccount.accountCurrency}}', type: 'string' },
  // Netted account (Compensación — resolves dynamically to cargar or abonar based on cashflow direction)
  { namespace: 'nettedAccount', id: 'bankName',        label: 'Netted: Bank Name',        labelKey: `${P}.nettedBankName`,        token: '{{nettedAccount.bankName}}',        type: 'string' },
  { namespace: 'nettedAccount', id: 'accountName',     label: 'Netted: Account Name',     labelKey: `${P}.nettedAccountName`,     token: '{{nettedAccount.accountName}}',     type: 'string' },
  { namespace: 'nettedAccount', id: 'accountNumber',   label: 'Netted: Account Number',   labelKey: `${P}.nettedAccountNumber`,   token: '{{nettedAccount.accountNumber}}',   type: 'string' },
  { namespace: 'nettedAccount', id: 'swiftCode',       label: 'Netted: SWIFT Code',       labelKey: `${P}.nettedSwiftCode`,       token: '{{nettedAccount.swiftCode}}',       type: 'string' },
  { namespace: 'nettedAccount', id: 'accountCurrency', label: 'Netted: Currency',         labelKey: `${P}.nettedCurrency`,        token: '{{nettedAccount.accountCurrency}}', type: 'string' },
  // Other settlement fields
  { namespace: 'settlement',    id: 'centralBankTradeCode', label: 'Central Bank Trade Code', labelKey: `${P}.centralBankTradeCode`, token: '{{settlement.centralBankTradeCode}}', type: 'string' },
  { namespace: 'settlement',    id: 'cashflowDirection',    label: 'Pagamos / Recibimos',     labelKey: `${P}.cashflowDirection`,    token: '{{settlement.cashflowDirection}}',    type: 'string' },
  { namespace: 'settlement',    id: 'nettedAmount',         label: 'Netted Amount',           labelKey: `${P}.nettedAmount`,         token: '{{settlement.nettedAmount}}',         type: 'number' },
  { namespace: 'settlement',    id: 'nettedCurrency',       label: 'Netted Currency',         labelKey: `${P}.nettedSettlementCurrency`, token: '{{settlement.nettedCurrency}}',       type: 'string' },
];

const MODIFICATION_PLACEHOLDERS: PlaceholderPaletteItem[] = [
  // General modification info (on the new replacement trade)
  { namespace: 'trade', id: 'modificationDetails.modificationType',    label: 'Modification Type',              labelKey: `${P}.modType`,                  token: '{{trade.modificationDetails.modificationType}}',                    type: 'string'  },
  { namespace: 'trade', id: 'modificationDetails.originalTradeNumber', label: 'Original Trade Number',          labelKey: `${P}.origTradeNumber`,           token: '{{trade.modificationDetails.originalTradeNumber}}',                 type: 'string'  },
  { namespace: 'trade', id: 'modificationDetails.confirmedByName',     label: 'Confirmed By',                   labelKey: `${P}.modConfirmedBy`,            token: '{{trade.modificationDetails.confirmedByName}}',                     type: 'string'  },
  { namespace: 'trade', id: 'modificationDetails.confirmedAt',         label: 'Confirmation Date',              labelKey: `${P}.modConfirmedAt`,            token: '{{trade.modificationDetails.confirmedAt}}',                         type: 'date'    },
  // Boolean type flags — clicking opens an output-value dialog before inserting
  { namespace: 'trade', id: 'modificationDetails.isRollover',          label: 'Is Rollover?',                   labelKey: `${P}.isRollover`,                token: '{{trade.modificationDetails.isRollover}}',                          type: 'boolean' },
  { namespace: 'trade', id: 'modificationDetails.isCancellation',      label: 'Is Cancellation?',               labelKey: `${P}.isCancellation`,            token: '{{trade.modificationDetails.isCancellation}}',                      type: 'boolean' },
  { namespace: 'trade', id: 'modificationDetails.isSettlementTypeChange', label: 'Is Settlement Type Change?',  labelKey: `${P}.isSettlementTypeChange`,    token: '{{trade.modificationDetails.isSettlementTypeChange}}',               type: 'boolean' },
  { namespace: 'trade', id: 'modificationDetails.isGroupingChange',    label: 'Is Grouping Change?',            labelKey: `${P}.isGroupingChange`,          token: '{{trade.modificationDetails.isGroupingChange}}',                    type: 'boolean' },
  { namespace: 'trade', id: 'modificationDetails.isOther',             label: 'Is Other?',                      labelKey: `${P}.isOther`,                   token: '{{trade.modificationDetails.isOther}}',                             type: 'boolean' },
  // Original field values (populated only for the fields that changed)
  { namespace: 'trade', id: 'modificationDetails.originalValues.MaturityDate',       label: 'Original Maturity Date',         labelKey: `${P}.origMaturityDate`,          token: '{{trade.modificationDetails.originalValues.MaturityDate}}',         type: 'date'   },
  { namespace: 'trade', id: 'modificationDetails.originalValues.Price',              label: 'Original Price',                 labelKey: `${P}.origPrice`,                 token: '{{trade.modificationDetails.originalValues.Price}}',                type: 'number' },
  { namespace: 'trade', id: 'modificationDetails.originalValues.SpotPrice',          label: 'Original Spot Price',            labelKey: `${P}.origSpotPrice`,             token: '{{trade.modificationDetails.originalValues.SpotPrice}}',            type: 'number' },
  { namespace: 'trade', id: 'modificationDetails.originalValues.ForwardPoints',      label: 'Original Forward Points',        labelKey: `${P}.origForwardPoints`,         token: '{{trade.modificationDetails.originalValues.ForwardPoints}}',        type: 'number' },
  { namespace: 'trade', id: 'modificationDetails.originalValues.SettlementType',     label: 'Original Settlement Type',       labelKey: `${P}.origSettlementType`,        token: '{{trade.modificationDetails.originalValues.SettlementType}}',       type: 'string' },
  { namespace: 'trade', id: 'modificationDetails.originalValues.SettlementCurrency', label: 'Original Settlement Currency',   labelKey: `${P}.origSettlementCurrency`,    token: '{{trade.modificationDetails.originalValues.SettlementCurrency}}',   type: 'string' },
  { namespace: 'trade', id: 'modificationDetails.originalValues.FixingReference',    label: 'Original Fixing Reference',      labelKey: `${P}.origFixingReference`,       token: '{{trade.modificationDetails.originalValues.FixingReference}}',      type: 'string' },
  { namespace: 'trade', id: 'modificationDetails.originalValues.BankTradeNumber',    label: 'Original Bank Trade Number',     labelKey: `${P}.origBankTradeNumber`,       token: '{{trade.modificationDetails.originalValues.BankTradeNumber}}',      type: 'string' },
];

// ============================================================
// Component
// ============================================================

interface PlaceholderPaletteProps {
  customPlaceholders: CustomPlaceholder[];
  onInsert: (item: PlaceholderPaletteItem) => void;
  groupingLabels?: { grouping1?: string; grouping2?: string; grouping3?: string };
}

interface PaletteGroupProps {
  title: string;
  items: PlaceholderPaletteItem[];
  onInsert: (item: PlaceholderPaletteItem) => void;
  defaultOpen?: boolean;
}

const PaletteGroup: React.FC<PaletteGroupProps> = ({ title, items, onInsert, defaultOpen = true }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="palette-group">
      <button
        className="palette-group-header"
        onClick={() => setIsOpen(!isOpen)}
        type="button"
      >
        <span className={`palette-chevron ${isOpen ? 'open' : ''}`}>&#9654;</span>
        <span className="palette-group-title">{title}</span>
        <span className="palette-group-count">{items.length}</span>
      </button>
      {isOpen && (
        <div className="palette-group-items">
          {items.map((item) => (
            <button
              key={item.token}
              className={`palette-item palette-item--${item.namespace}`}
              onClick={() => onInsert(item)}
              title={item.token}
              type="button"
            >
              <span className="palette-item-label">
                {item.labelKey ? t(item.labelKey, item.label) : item.label}
              </span>
              <span className="palette-item-type">{item.type}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const PlaceholderPalette: React.FC<PlaceholderPaletteProps> = ({ customPlaceholders, onInsert, groupingLabels }) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  // Boolean token insert dialog
  const [pendingBoolItem, setPendingBoolItem] = useState<PlaceholderPaletteItem | null>(null);
  const [trueVal, setTrueVal] = useState('X');
  const [falseVal, setFalseVal] = useState('');

  const handleInsert = (item: PlaceholderPaletteItem) => {
    if (item.type === 'boolean') {
      setPendingBoolItem(item);
      setTrueVal('X');
      setFalseVal('');
      return;
    }
    onInsert(item);
  };

  const handleBoolInsert = () => {
    if (!pendingBoolItem) return;
    // Append |trueVal|falseVal before the closing }}
    const base = pendingBoolItem.token.slice(0, -2);
    const finalToken = `${base}|${trueVal}|${falseVal}}}`;
    onInsert({ ...pendingBoolItem, token: finalToken });
    setPendingBoolItem(null);
  };

  // Apply client-defined Grouping 1/2/3 labels
  const tradeFields = useMemo(() => {
    if (!groupingLabels?.grouping1 && !groupingLabels?.grouping2 && !groupingLabels?.grouping3) return TRADE_FIELD_PLACEHOLDERS;
    return TRADE_FIELD_PLACEHOLDERS.map((item) => {
      if (item.id === 'Grouping1' && groupingLabels.grouping1) {
        return { ...item, label: groupingLabels.grouping1, labelKey: undefined };
      }
      if (item.id === 'Grouping2' && groupingLabels.grouping2) {
        return { ...item, label: groupingLabels.grouping2, labelKey: undefined };
      }
      if (item.id === 'Grouping3' && groupingLabels.grouping3) {
        return { ...item, label: groupingLabels.grouping3, labelKey: undefined };
      }
      return item;
    });
  }, [groupingLabels]);

  const customItems: PlaceholderPaletteItem[] = useMemo(() =>
    customPlaceholders.map((p) => ({
      namespace: 'custom' as const,
      id: p.id,
      label: p.label,
      token: p.token,
      type: p.type === 'IMAGE' ? 'image' as const : 'string' as const,
    })),
    [customPlaceholders]
  );

  const filterItems = (items: PlaceholderPaletteItem[]) => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter((item) => {
      const resolvedLabel = item.labelKey ? t(item.labelKey, item.label) : item.label;
      return resolvedLabel.toLowerCase().includes(q) || item.token.toLowerCase().includes(q);
    });
  };

  const filteredTrade = filterItems(tradeFields);
  const filteredSystem = filterItems(SYSTEM_PLACEHOLDERS);
  const filteredCustom = filterItems(customItems);
  const filteredSettlement = filterItems(SETTLEMENT_PLACEHOLDERS);
  const filteredModification = filterItems(MODIFICATION_PLACEHOLDERS);

  const boolDialogLabel = pendingBoolItem
    ? (pendingBoolItem.labelKey ? t(pendingBoolItem.labelKey, pendingBoolItem.label) : pendingBoolItem.label)
    : '';

  return (
    <>
      <div className="placeholder-palette">
        <input
          type="text"
          className="palette-search"
          placeholder={t('admin.customDocuments.editor.palette.search', 'Search placeholders...')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="palette-groups">
          <PaletteGroup
            title={t('admin.customDocuments.editor.palette.groups.trade', 'Trade Fields')}
            items={filteredTrade}
            onInsert={handleInsert}
          />
          <PaletteGroup
            title={t('admin.customDocuments.editor.palette.groups.system', 'System Values')}
            items={filteredSystem}
            onInsert={handleInsert}
          />
          <PaletteGroup
            title={t('admin.customDocuments.editor.palette.groups.settlement', 'Settlement')}
            items={filteredSettlement}
            onInsert={handleInsert}
            defaultOpen={false}
          />
          <PaletteGroup
            title={t('admin.customDocuments.editor.palette.groups.modification', 'Modification')}
            items={filteredModification}
            onInsert={handleInsert}
            defaultOpen={false}
          />
          <PaletteGroup
            title={t('admin.customDocuments.editor.palette.groups.custom', 'Custom')}
            items={filteredCustom}
            onInsert={handleInsert}
            defaultOpen={customItems.length > 0}
          />
          {customItems.length === 0 && (
            <p className="palette-no-custom">
              {t('admin.customDocuments.editor.palette.noCustom', 'No custom placeholders defined.')}{' '}
              {t('admin.customDocuments.editor.palette.noCustomLink', 'Add them in the Custom Placeholders section.')}
            </p>
          )}
        </div>
      </div>

      {pendingBoolItem && (
        <div className="modal-overlay" onClick={() => setPendingBoolItem(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="form-panel-header">
              <h4>{t('admin.customDocuments.editor.palette.boolDialog.title')}</h4>
              <button className="btn-close" onClick={() => setPendingBoolItem(null)}>&times;</button>
            </div>
            <div className="form-panel-body">
              <p className="form-hint">
                {t('admin.customDocuments.editor.palette.boolDialog.hint', { field: boolDialogLabel })}
              </p>
              <div className="form-group">
                <label>{t('admin.customDocuments.editor.palette.boolDialog.trueValue')}</label>
                <input
                  className="form-input"
                  value={trueVal}
                  onChange={e => setTrueVal(e.target.value)}
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>
                  {t('admin.customDocuments.editor.palette.boolDialog.falseValue')}
                  {' '}
                  <span className="form-hint-inline">
                    {t('admin.customDocuments.editor.palette.boolDialog.blankHint')}
                  </span>
                </label>
                <input
                  className="form-input"
                  value={falseVal}
                  onChange={e => setFalseVal(e.target.value)}
                />
              </div>
            </div>
            <div className="form-panel-footer">
              <button className="btn-secondary" onClick={() => setPendingBoolItem(null)}>
                {t('common.cancel', 'Cancel')}
              </button>
              <button className="btn-primary" onClick={handleBoolInsert}>
                {t('admin.customDocuments.editor.palette.boolDialog.insert')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PlaceholderPalette;
